import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import {
  getAllTests,
  getAllOrganizations,
  getUserProfile,
  getAllTestsByOrg,
} from "./firestoreService";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
} from "@mui/material";

function CorrectionsQueue() {
  const { userRole, currentUser } = useAuth();
  const [allTests, setAllTests] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const { t } = useTranslation();

  const requestedTestIds = location.state?.requestedTestIds;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      let testsPromise;
      // Superuser gets all tests, admin gets tests for their org
      if (userRole === "superuser") {
        testsPromise = getAllTests();
        const orgs = await getAllOrganizations();
        setOrganizations(orgs.filter((org) => org.id !== "dd_quick_platform"));
      } else {
        // 'admin' role
        const profile = await getUserProfile(currentUser.uid);
        if (!profile || !profile.organizationId)
          throw new Error("Admin organization not found.");
        testsPromise = getAllTestsByOrg(profile.organizationId);
      }

      const allFetchedTests = await testsPromise;

      // Filter the fetched tests to show only those with open correction requests
      if (requestedTestIds) {
        const requestedIdsSet = new Set(requestedTestIds);
        setAllTests(
          allFetchedTests.filter((test) => requestedIdsSet.has(test.id))
        );
      } else {
        // If no IDs are passed (e.g., direct navigation), show an empty list
        setAllTests([]);
      }
    } catch (err) {
      console.error("Error fetching data for corrections queue:", err);
      setError(t("corrections.loadError"));
    } finally {
      setLoading(false);
    }
  }, [userRole, currentUser, requestedTestIds, t]);

  useEffect(() => {
    // Only fetch if we have the necessary info
    if (userRole && currentUser && requestedTestIds) {
      fetchData();
    } else {
      setLoading(false); // Nothing to load
    }
  }, [userRole, currentUser, requestedTestIds, fetchData]);

  if (loading)
    return <CircularProgress sx={{ display: "block", margin: "2rem auto" }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t("corrections.title")}
      </Typography>
      <Typography paragraph color="text.secondary">
        {t("corrections.description")}
      </Typography>

      <Paper elevation={2} sx={{ mt: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("findTest.caseId")}</TableCell>
                <TableCell>{t("findTest.dateSubmitted")}</TableCell>
                {userRole === "superuser" && (
                  <TableCell>{t("common.organization")}</TableCell>
                )}
                <TableCell align="right">{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allTests.length > 0 ? (
                allTests.map((test) => (
                  <TableRow key={test.id} hover>
                    <TableCell>{test.caseId}</TableCell>
                    <TableCell>
                      {new Date(
                        test.timestamp.seconds * 1000
                      ).toLocaleDateString()}
                    </TableCell>
                    {userRole === "superuser" && (
                      <TableCell>
                        {organizations.find((o) => o.id === test.organizationId)
                          ?.shortName || "N/A"}
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            navigate(`/app/view-test/${test.id}`, {
                              state: { requestedTestIds },
                            })
                          }
                        >
                          {t("common.view")}
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() =>
                            navigate(`/app/edit-test/${test.id}`, {
                              state: { requestedTestIds },
                            })
                          }
                        >
                          {t("common.edit")}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={userRole === "superuser" ? 5 : 4}
                    align="center"
                  >
                    <Typography
                      color="text.secondary"
                      sx={{ fontStyle: "italic", p: 3 }}
                    >
                      {t("corrections.noRequests")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
}

export default CorrectionsQueue;
